import React, { useMemo } from 'react';
import { Badge, Group, Paper, SimpleGrid, Stack, Text } from '@mantine/core';

const find = (arr = [], name) => {
  const normalize = (value) => String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  const needle = normalize(name);
  const entry = arr.find((ch) => normalize(ch.name) === needle || normalize(ch.name).includes(needle));
  return entry?.value ?? entry?.state ?? null;
};

const isOn = (val) => {
  if (val === null || val === undefined) return false;
  if (typeof val === 'boolean') return val;
  if (typeof val === 'number') return val > 0;
  const s = String(val).toLowerCase();
  return s === '1' || s === 'true' || s === 'on' || s === 'yes' || s === 'running';
};

const fmtVal = (val, unit = '') => {
  if (val === null || val === undefined || val === '') return '--';
  const n = parseFloat(val);
  if (Number.isNaN(n)) return String(val);
  return `${n.toFixed(1)}${unit}`;
};

const fmtPct = (val) => {
  const n = parseFloat(val);
  if (Number.isNaN(n)) return 0;
  return Math.max(0, Math.min(100, n));
};

const TOKENS = {
  bg: '#f4f7fb',
  panel: '#ffffff',
  panelSoft: '#f8fafc',
  panelBorder: '#d7e0ea',
  text: '#16324f',
  textSoft: '#6f859d',
  blue: '#2383e2',
  blueSoft: '#eaf4ff',
  green: '#1dbf73',
  greenSoft: '#dcf7ea',
  red: '#e45858',
  redSoft: '#fde8e8',
  amber: '#d48b1f',
  pipe: '#a9b9ca',
  pipeActive: '#3ca4ff',
};

const MetricTile = ({ label, value, tone = 'blue' }) => {
  const palette = tone === 'green'
    ? { bg: TOKENS.greenSoft, text: TOKENS.green }
    : tone === 'red'
      ? { bg: TOKENS.redSoft, text: TOKENS.red }
      : { bg: TOKENS.blueSoft, text: TOKENS.blue };

  return (
    <div
      style={{
        background: palette.bg,
        border: `1px solid ${TOKENS.panelBorder}`,
        borderRadius: 12,
        padding: '12px 14px',
        minHeight: 74,
      }}
    >
      <Text size="xs" color={TOKENS.textSoft} tt="uppercase" fw={700} style={{ letterSpacing: 0.3 }}>
        {label}
      </Text>
      <Text mt={6} size="xl" fw={700} color={palette.text}>
        {value}
      </Text>
    </div>
  );
};

const IndicatorPill = ({ label, ok }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      padding: '8px 10px',
      borderRadius: 10,
      background: ok ? TOKENS.greenSoft : TOKENS.redSoft,
      border: `1px solid ${ok ? '#bee7d3' : '#f5c4c4'}`,
    }}
  >
    <div
      style={{
        width: 10,
        height: 10,
        borderRadius: '50%',
        background: ok ? TOKENS.green : TOKENS.red,
        boxShadow: ok ? '0 0 0 4px rgba(29,191,115,0.12)' : '0 0 0 4px rgba(228,88,88,0.12)',
      }}
    />
    <div>
      <Text size="xs" color={TOKENS.textSoft} fw={700}>
        {label}
      </Text>
      <Text size="xs" color={ok ? TOKENS.green : TOKENS.red} fw={700}>
        {ok ? 'Normal' : 'Tripped'}
      </Text>
    </div>
  </div>
);

const SensorCard = ({ label, value, unit, accent }) => (
  <div
    style={{
      background: TOKENS.panel,
      border: `1px solid ${TOKENS.panelBorder}`,
      borderLeft: `4px solid ${accent}`,
      borderRadius: 12,
      padding: '12px 14px',
    }}
  >
    <Text size="xs" color={TOKENS.textSoft} tt="uppercase" fw={700}>
      {label}
    </Text>
    <Text mt={6} size="lg" fw={700} color={TOKENS.text}>
      {fmtVal(value, unit)}
    </Text>
  </div>
);

const FlowDots = ({ points, active }) => (
  <>
    {active && points.map((point, idx) => (
      <circle key={`${point.cx}-${point.cy}`} cx={point.cx} cy={point.cy} r={5} fill={TOKENS.blue}>
        <animate
          attributeName="opacity"
          values="0;1;0"
          dur="1.6s"
          begin={`${idx * 0.32}s`}
          repeatCount="indefinite"
        />
      </circle>
    ))}
  </>
);

const MachineSchematic = ({ statusData = {} }) => {
  const di = useMemo(() => statusData.digital_input || [], [statusData]);
  const ai = useMemo(() => statusData.analog_input || [], [statusData]);
  const ao = useMemo(() => statusData.analog_output || [], [statusData]);
  const others = useMemo(() => statusData.others || [], [statusData]);
  const allAnalog = useMemo(() => [...ai, ...ao, ...others], [ai, ao, others]);

  const compressorOn = useMemo(() => isOn(find(di, 'compressor')), [di]);
  const pumpOn = useMemo(() => isOn(find(di, 'pump1')) || isOn(find(di, 'pump2')) || isOn(find(di, 'pump')), [di]);
  const lpOk = useMemo(() => !isOn(find(di, 'lp')), [di]);
  const hpOk = useMemo(() => !isOn(find(di, 'hp')), [di]);
  const olrOk = useMemo(() => !isOn(find(di, 'olr')), [di]);

  const temperature = useMemo(() => find(allAnalog, 'temperature'), [allAnalog]);
  const humidity = useMemo(() => find(allAnalog, 'humidity'), [allAnalog]);
  const dewPoint = useMemo(() => find(allAnalog, 'dewpoint'), [allAnalog]);
  const tankLevel = useMemo(
    () => fmtPct(find(allAnalog, 'tanklevel') ?? find(allAnalog, 'waterlevel') ?? find(allAnalog, 'water level')),
    [allAnalog]
  );
  const dailyYield = useMemo(
    () => find(allAnalog, 'dailywatergenerated') ?? find(allAnalog, 'daily water generated') ?? find(allAnalog, 'yield'),
    [allAnalog]
  );
  const runtime = useMemo(() => find(allAnalog, 'runtime') ?? find(allAnalog, 'run time'), [allAnalog]);

  const healthState = compressorOn ? 'Running' : (pumpOn ? 'Circulating' : 'Idle');
  const healthTone = compressorOn ? 'green' : (pumpOn ? 'blue' : 'red');

  const fillHeight = 184 * (tankLevel / 100);
  const fillY = 278 - fillHeight;

  return (
    <Paper
      withBorder
      radius="md"
      p="lg"
      sx={{
        background: `linear-gradient(180deg, ${TOKENS.panel} 0%, ${TOKENS.bg} 100%)`,
        borderColor: TOKENS.panelBorder,
        overflow: 'hidden',
      }}
    >
      <Stack spacing="lg">
        <Group position="apart" align="flex-start">
          <div>
            <Group spacing={8}>
              <Text size="lg" fw={700} color={TOKENS.text}>
                Digital Twin
              </Text>
              <Badge
                size="md"
                radius="sm"
                variant="light"
                color={healthTone}
                styles={{ root: { textTransform: 'none' } }}
              >
                {healthState}
              </Badge>
            </Group>
            <Text size="sm" color={TOKENS.textSoft} mt={4}>
              Live machine flow, interlocks, tank state, and environmental readings
            </Text>
          </div>
        </Group>

        <SimpleGrid cols={4} breakpoints={[{ maxWidth: 'md', cols: 2 }, { maxWidth: 'xs', cols: 1 }]}>
          <MetricTile label="Compressor" value={compressorOn ? 'Online' : 'Offline'} tone={compressorOn ? 'green' : 'red'} />
          <MetricTile label="Pump" value={pumpOn ? 'Running' : 'Stopped'} tone={pumpOn ? 'green' : 'red'} />
          <MetricTile label="Tank Level" value={`${Math.round(tankLevel)}%`} tone="blue" />
          <MetricTile label="Daily Yield" value={fmtVal(dailyYield, ' L')} tone="blue" />
        </SimpleGrid>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1.7fr) minmax(260px, 0.9fr)',
            gap: 18,
            alignItems: 'stretch',
          }}
        >
          <div
            style={{
              background: TOKENS.panelSoft,
              border: `1px solid ${TOKENS.panelBorder}`,
              borderRadius: 16,
              padding: 14,
              overflow: 'auto',
            }}
          >
            <svg
              viewBox="0 0 860 420"
              style={{ width: '100%', minWidth: 640, display: 'block' }}
              xmlns="http://www.w3.org/2000/svg"
            >
              <defs>
                <linearGradient id="tankFill" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stopColor="#8bd0ff" />
                  <stop offset="100%" stopColor="#2383e2" />
                </linearGradient>
                <filter id="panelGlow" x="-50%" y="-50%" width="200%" height="200%">
                  <feGaussianBlur stdDeviation="6" result="blur" />
                  <feMerge>
                    <feMergeNode in="blur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
              </defs>

              <rect x="0" y="0" width="860" height="420" rx="24" fill="#f8fbff" />

              <line x1="170" y1="136" x2="295" y2="92" stroke={compressorOn ? TOKENS.pipeActive : TOKENS.pipe} strokeWidth="8" strokeLinecap="round" />
              <line x1="330" y1="132" x2="330" y2="248" stroke={compressorOn ? TOKENS.pipeActive : TOKENS.pipe} strokeWidth="8" strokeLinecap="round" />
              <line x1="295" y1="286" x2="170" y2="208" stroke={compressorOn ? TOKENS.pipeActive : TOKENS.pipe} strokeWidth="8" strokeLinecap="round" />
              <line x1="486" y1="252" x2="610" y2="252" stroke={(compressorOn || pumpOn) ? TOKENS.pipeActive : TOKENS.pipe} strokeWidth="8" strokeLinecap="round" />
              <line x1="698" y1="252" x2="782" y2="252" stroke={pumpOn ? TOKENS.pipeActive : TOKENS.pipe} strokeWidth="8" strokeLinecap="round" />

              <FlowDots
                active={compressorOn}
                points={[
                  { cx: 212, cy: 122 },
                  { cx: 252, cy: 107 },
                  { cx: 290, cy: 95 },
                ]}
              />
              <FlowDots
                active={compressorOn}
                points={[
                  { cx: 330, cy: 170 },
                  { cx: 330, cy: 206 },
                  { cx: 330, cy: 238 },
                ]}
              />
              <FlowDots
                active={compressorOn}
                points={[
                  { cx: 254, cy: 259 },
                  { cx: 214, cy: 233 },
                  { cx: 182, cy: 214 },
                ]}
              />
              <FlowDots
                active={compressorOn || pumpOn}
                points={[
                  { cx: 530, cy: 252 },
                  { cx: 570, cy: 252 },
                ]}
              />
              <FlowDots
                active={pumpOn}
                points={[
                  { cx: 725, cy: 252 },
                  { cx: 758, cy: 252 },
                ]}
              />

              <rect
                x="58"
                y="124"
                width="112"
                height="92"
                rx="18"
                fill={compressorOn ? TOKENS.greenSoft : '#edf2f7'}
                stroke={compressorOn ? TOKENS.green : TOKENS.panelBorder}
                strokeWidth="3"
                filter={compressorOn ? 'url(#panelGlow)' : undefined}
              />
              <text x="114" y="164" textAnchor="middle" fontSize="20" fill={TOKENS.text} fontWeight="700">Compressor</text>
              <text x="114" y="190" textAnchor="middle" fontSize="15" fill={compressorOn ? TOKENS.green : TOKENS.textSoft} fontWeight="700">
                {compressorOn ? 'ONLINE' : 'OFFLINE'}
              </text>

              <rect x="295" y="60" width="108" height="72" rx="14" fill="#f4f7fb" stroke={TOKENS.panelBorder} strokeWidth="2.5" />
              <text x="349" y="98" textAnchor="middle" fontSize="20" fill={TOKENS.text} fontWeight="600">Condenser</text>
              {[0, 1, 2, 3].map((idx) => (
                <line
                  key={`cond-${idx}`}
                  x1="312"
                  y1={76 + idx * 14}
                  x2="386"
                  y2={76 + idx * 14}
                  stroke="#b8c6d6"
                  strokeWidth="2.5"
                />
              ))}

              <rect x="295" y="250" width="108" height="72" rx="14" fill="#eef7ff" stroke={TOKENS.blue} strokeWidth="2.5" />
              <text x="349" y="288" textAnchor="middle" fontSize="20" fill={TOKENS.text} fontWeight="600">Evaporator</text>
              {[0, 1, 2, 3].map((idx) => (
                <line
                  key={`eva-${idx}`}
                  x1="312"
                  y1={266 + idx * 14}
                  x2="386"
                  y2={266 + idx * 14}
                  stroke="#9ed0ff"
                  strokeWidth="2.5"
                />
              ))}

              <circle
                cx="460"
                cy="340"
                r="30"
                fill={pumpOn ? TOKENS.greenSoft : '#f4f7fb'}
                stroke={pumpOn ? TOKENS.green : TOKENS.panelBorder}
                strokeWidth="3"
                filter={pumpOn ? 'url(#panelGlow)' : undefined}
              />
              <text x="460" y="338" textAnchor="middle" fontSize="18" fill={TOKENS.text} fontWeight="700">Pump</text>
              <text x="460" y="359" textAnchor="middle" fontSize="13" fill={pumpOn ? TOKENS.green : TOKENS.textSoft} fontWeight="700">
                {pumpOn ? 'RUNNING' : 'STOPPED'}
              </text>

              <rect x="610" y="94" width="88" height="184" rx="18" fill="#f4f7fb" stroke={TOKENS.blue} strokeWidth="4" />
              <rect x="616" y={fillY} width="76" height={fillHeight} rx="12" fill="url(#tankFill)" style={{ transition: 'height 0.6s ease, y 0.6s ease' }} />
              <text x="654" y="82" textAnchor="middle" fontSize="18" fill={TOKENS.textSoft} fontWeight="700">Water Tank</text>
              <text x="654" y="174" textAnchor="middle" fontSize="36" fill={TOKENS.text} fontWeight="800">
                {Math.round(tankLevel)}%
              </text>
              {[0, 1, 2].map((idx) => (
                <line
                  key={`tank-${idx}`}
                  x1="618"
                  y1={134 + idx * 44}
                  x2="690"
                  y2={134 + idx * 44}
                  stroke="#97bce1"
                  strokeDasharray="6 6"
                />
              ))}

              <text x="790" y="246" fontSize="16" fill={TOKENS.textSoft} fontWeight="600">Outlet</text>
              <text x="790" y="266" fontSize="13" fill={pumpOn ? TOKENS.blue : TOKENS.textSoft}>
                {pumpOn ? 'Flow active' : 'Standby'}
              </text>
            </svg>
          </div>

          <Stack spacing="md">
            <div
              style={{
                background: TOKENS.panel,
                border: `1px solid ${TOKENS.panelBorder}`,
                borderRadius: 16,
                padding: 16,
              }}
            >
              <Text size="sm" fw={700} color={TOKENS.text} mb={12}>
                Environmental Sensors
              </Text>
              <Stack spacing="sm">
                <SensorCard label="Temperature" value={temperature} unit=" °C" accent={TOKENS.blue} />
                <SensorCard label="Humidity" value={humidity} unit=" %" accent={TOKENS.green} />
                <SensorCard label="Dew Point" value={dewPoint} unit=" °C" accent={TOKENS.amber} />
              </Stack>
            </div>

            <div
              style={{
                background: TOKENS.panel,
                border: `1px solid ${TOKENS.panelBorder}`,
                borderRadius: 16,
                padding: 16,
              }}
            >
              <Text size="sm" fw={700} color={TOKENS.text} mb={12}>
                Protection & Runtime
              </Text>
              <Stack spacing="sm">
                <SimpleGrid cols={1}>
                  <IndicatorPill label="Low Pressure" ok={lpOk} />
                  <IndicatorPill label="High Pressure" ok={hpOk} />
                  <IndicatorPill label="Overload Relay" ok={olrOk} />
                </SimpleGrid>
                <div
                  style={{
                    borderRadius: 12,
                    border: `1px solid ${TOKENS.panelBorder}`,
                    background: TOKENS.panelSoft,
                    padding: '12px 14px',
                  }}
                >
                  <Text size="xs" color={TOKENS.textSoft} tt="uppercase" fw={700}>
                    Run Time
                  </Text>
                  <Text mt={6} size="lg" fw={700} color={TOKENS.text}>
                    {runtime || '--'}
                  </Text>
                </div>
              </Stack>
            </div>
          </Stack>
        </div>
      </Stack>
    </Paper>
  );
};

export default MachineSchematic;
